<template>
    <rkm-form
        v-if="model"
        ref="RkmForm"
        show-legend
    >
        <rkm-row>
            <rkm-col cols="6">
                <rkm-form-input
                    :validations="validations.nome"
                    label="Nome"
                    v-model="model.nome"
                />
            </rkm-col>

            <rkm-col cols="6">
                <rkm-form-input-autocomplete
                    :repository="repositories.ocupacao"
                    :search-fields="fields.ocupacao"
                    :text-field="(item) => item.codigo ? `${item.codigo} - ${item.nome}` : null"
                    :validations="validations.ocupacao"
                    label="CBO"
                    v-model="model.ocupacao"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col>
                <rkm-form-select
                    :options="form.sexos"
                    @change="model.permiteAgendamentoGestante = false"
                    default-option-text="AMBOS"
                    label="Sexo"
                    show-default-option
                    text-field="descricao"
                    v-model="model.sexoId"
                    value-field="id"
                />
            </rkm-col>

            <rkm-col>
                <rkm-form-input
                    :validations="validations.idadeMinima"
                    label="Idade mínima"
                    number
                    v-model="model.idadeMinima"
                />
            </rkm-col>

            <rkm-col>
                <rkm-form-input
                    :validations="validations.idadeMaxima"
                    label="Idade máxima"
                    number
                    v-model="model.idadeMaxima"
                />
            </rkm-col>

            <rkm-col>
                <rkm-form-input
                    :validations="validations.limiteConsulta"
                    label="Limite de consulta(s)"
                    number
                    v-model="model.limiteConsulta"
                />
            </rkm-col>

            <rkm-col>
                <rkm-form-input
                    :validations="validations.limiteConsultaMes"
                    label="Limite em mes(es)"
                    number
                    v-model="model.limiteConsultaMes"
                />
            </rkm-col>

            <rkm-col>
                <rkm-form-select
                    :disabled="!model.isRegulado"
                    :label="false"
                    :options="form.regulacaoTipos"
                    :validations="validations.regulacaoTipo"
                    name="Tipo de regulação"
                    show-default-option
                    text-field="nome"
                    v-model="model.regulacaoTipoId"
                    value-field="id"
                >
                    <rkm-input-group-prepend slot="prepend">
                        <rkm-input-group-text>
                            <rkm-form-checkbox
                                @change="model.regulacaoTipoId = null"
                                v-model="model.isRegulado"
                            >
                                Regulado
                            </rkm-form-checkbox>
                        </rkm-input-group-text>
                    </rkm-input-group-prepend>
                </rkm-form-select>
            </rkm-col>
        </rkm-row>

         <rkm-row>
            <rkm-col cols="12">
                <rkm-form-input-autocomplete
                    :disabled="!model.ocupacao"
                    :repository="repositories.procedimento"
                    :request-query-string-params="{
                        cbo: model.ocupacao ? model.ocupacao.codigo : null,
                        instrumento_registro: enums.registro.BPA_CONSOLIDADO,
                        search_by_apelido: searchByApelido,
                    }"
                    :search-fields="fields.procedimento"
                    :text-field="(item) => item.codigo ? `${formatProcedimento(item.codigo)} - ${item.nome}` : null"
                    label="Procedimento de grupo"
                    v-model="model.procedimentoGrupo"
                >
                    <rkm-input-group-append slot="append">
                        <rkm-input-group-text>
                            <rkm-form-radio-group v-model="searchByApelido">
                                <rkm-form-radio :value="false">Código / Nome</rkm-form-radio>
                                <rkm-form-radio :value="true">Apelido</rkm-form-radio>
                            </rkm-form-radio-group>
                        </rkm-input-group-text>
                    </rkm-input-group-append>
                </rkm-form-input-autocomplete>
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col cols="12">
                <rkm-form-textarea
                    :validations="validations.mensagemProtocoloAgendamento"
                    label="Mensagem de protocolo do agendamento"
                    v-model="model.mensagemProtocoloAgendamento"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col cols="4">
                <rkm-form-checkbox v-model="model.permiteFechamentoSemProcedimento">
                    Permite fechamento sem procedimento
                </rkm-form-checkbox>

                <rkm-form-checkbox
                    :disabled="model.sexoId === enums.sexo.MASCULINO"
                    v-model="model.permiteAgendamentoGestante"
                >
                    Permite agendamento para gestante
                </rkm-form-checkbox>

                <rkm-form-checkbox v-model="model.permiteAgendamentoMesmoDia">
                    Permite agendamento para o mesmo dia
                </rkm-form-checkbox>
            </rkm-col>

            <rkm-col cols="4">
                <rkm-form-checkbox v-model="model.permiteAgendamentoNaoMunicipe">
                    Permite agendamento para não munícipe
                </rkm-form-checkbox>

                <rkm-form-checkbox v-model="model.tratamento">
                    Permite agendar tratamento
                </rkm-form-checkbox>
            </rkm-col>

            <rkm-col cols="4">
                <rkm-form-checkbox v-model="model.encaminhamento">
                    Permite ser encaminhada para atendimento
                </rkm-form-checkbox>

                <rkm-form-checkbox
                    :disabled="!model.tratamento"
                    v-model="model.precisaInformarConclusaoTratamento"
                >
                    Precisa informar conclusão do tratamento
                </rkm-form-checkbox>
            </rkm-col>
        </rkm-row>
    </rkm-form>
</template>

<script>
// Enums.
import registro from '@/enums/sigtap/registro'
import sexo from '@/enums/cadastro/cidadao/sexo'

// Mixins.
import {error, formats, repository, stringCase} from '@/mixins'

// Vuex.
import {mapActions} from 'vuex'

export default {
    computed: {
        fields() {
            return {
                procedimento: this.searchByApelido
                    ? [{key: 'parametrizacao_procedimento.apelido', searchable: true}]
                    : [{key: 'codigo', searchable: true}, {key: 'nome', searchable: true}],
                ocupacao: [
                    {key: 'codigo', searchable: true},
                    {key: 'nome', searchable: true},
                ],
            }
        },
        validations() {
            return {
                nome: {
                    required: true,
                    min: 3,
                    max: 100,
                },
                ocupacao: {required: true},
                idadeMinima: {
                    max: 3,
                    min_value: 1,
                    max_value: this.model.idadeMaxima || 130,
                },
                idadeMaxima: {
                    max: 3,
                    min_value: this.model.idadeMinima || 1,
                    max_value: 130,
                },
                limiteConsulta: {
                    required: !!this.model.limiteConsultaMes,
                    max: 4,
                    min_value: 1,
                    max_value: 9999,
                },
                limiteConsultaMes: {
                    required: !!this.model.limiteConsulta,
                    max: 2,
                    min_value: 1,
                    max_value: 12,
                },
                mensagemProtocoloAgendamento: {max: 500},
                regulacaoTipo: {
                    required: this.model.isRegulado,
                },
            }
        },
    },
    created() {
        this.repositories = {
            especialidade: this.getConectaSusRepository('cadastro.especialidade'),
            ocupacao: this.getConectaSusRepository('sigtap.ocupacao'),
            procedimento: this.getConectaSusRepository('sigtap.procedimento'),
        }
    },
    data: () => ({
        enums: {
            registro,
            sexo,
        },
        form: null,
        model: null,
        searchByApelido: false,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        create() {
            this.repositories.especialidade.create()
                .then(response => {
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)
                    this.addWathOcupacao()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        store(request) {
            this.repositories.especialidade.store(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.showSaveNotification()
                    this.$emit('saved', response)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        edit(id) {
            this.repositories.especialidade.edit(id)
                .then(response => {
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)
                    this.addWathOcupacao()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        update(id, request) {
            this.repositories.especialidade
                .update(id, this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.showSaveNotification()
                    this.$emit('saved', response)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        load() {
            this.setLoading()

            if (this.id) {
                this.edit(this.id)
            } else {
                this.create()
            }
        },
        save() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    this.setLoading()

                    const request = {
                        encaminhamento: this.model.encaminhamento,
                        idadeMaxima: this.model.idadeMaxima,
                        idadeMinima: this.model.idadeMinima,
                        isRegulado: this.model.isRegulado,
                        limiteConsulta: this.model.limiteConsulta,
                        limiteConsultaMes: this.model.limiteConsultaMes,
                        mensagemProtocoloAgendamento: this.model.mensagemProtocoloAgendamento,
                        nome: this.model.nome,
                        ocupacaoId: this.model.ocupacao?.id,
                        permiteAgendamentoGestante: this.model.permiteAgendamentoGestante,
                        permiteAgendamentoMesmoDia: this.model.permiteAgendamentoMesmoDia,
                        permiteAgendamentoNaoMunicipe: this.model.permiteAgendamentoNaoMunicipe,
                        permiteFechamentoSemProcedimento: this.model.permiteFechamentoSemProcedimento,
                        precisaInformarConclusaoTratamento: this.model.precisaInformarConclusaoTratamento,
                        procedimentoGrupoCodigo: this.model.procedimentoGrupo?.codigo,
                        regulacaoTipoId: this.model.regulacaoTipoId,
                        sexoId: this.model.sexoId,
                        tratamento: this.model.tratamento,
                    }

                    if (this.id) {
                        this.update(this.id, request)
                    } else {
                        this.store(request)
                    }
                })
        },
        addWathOcupacao() {
            this.$watch('model.ocupacao', function () {
                this.model.procedimentoGrupo = null
            })
        },
    },
    mixins: [error, formats, repository, stringCase],
    mounted() {
        this.load()
    },
    props: {
        id: {type: [Number, String], default: null},
    },
    watch: {
        'model.tratamento'() {
            this.model.precisaInformarConclusaoTratamento = false
        },
    }
}
</script>